import { useState, useRef } from "react";
import { useSearchParams } from "react-router";
import { useCategories } from "../api/swr/useCategories";
import { useTiers } from "../api/swr/useTiers";
import { Button } from "~/atoms/button";
import { TierModal } from "../organisms/TierModal";
import HeadingPage from "../molecules/HeadingPage";
import { CenterSpinner } from "../atoms/spinner";
import { useTranslation } from "react-i18next";
import { getChartData } from "../helpers/chart";
import ChartDocuments from "../organisms/ChartDocuments";
import { ChartLinkedDocumentsContext } from "../organisms/DocumentCard";
import { useUserContext } from "../hooks/useUserContext";
import { PlatformRole } from "../config/types";
import type { ChartData } from "../helpers/chart";
import type { CategoryType } from "../api/types";
import { Info } from "lucide-react";
import { DocumentMetadataSheet } from "~/molecules/DocumentMetadataSheet";
import { ManageCategoriesButton } from "~/molecules/ManageCategoriesButton";
import { TagsModal } from "~/organisms/TagsModal";
import {
  StickyHorizontalScrollbar,
  hiddenNativeScrollbarClass,
} from "~/molecules/StickyHorizontalScrollbar";
import {
  filterDocuments,
  type DateFilterKey,
  type MultiSelectFilterKey,
} from "~/helpers/documentFilters";
import { DocumentActiveFilterChips } from "~/organisms/filters/DocumentActiveFilterChips";
import { DocumentFiltersPanel } from "~/organisms/filters/DocumentFiltersPanel";
import { FilterableContentLayout } from "~/organisms/filters/FilterableContentLayout";
import { useDocuments } from "~/api/v2/useDocuments";
import { useDocumentFilters } from "~/hooks/useDocumentFilters";
import SwitchWithLabel from "~/molecules/SwitchWithLabel";
import { FilterToggleButton } from "~/molecules/FilterToggleButton";
import { Toolbar } from "~/molecules/Toolbar";

const CHART_MULTI_SELECT_FILTER_KEYS = [
  "category",
  "tag",
  "owner",
  "publisher",
] as const satisfies readonly MultiSelectFilterKey[];

const CHART_DATE_FILTER_KEYS = [
  "publishedAt",
  "adoptedAt",
] as const satisfies readonly DateFilterKey[];

const Chart = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: categories } = useCategories();
  const { data: tiers } = useTiers();
  const { data: documents, mutate: mutateDocuments } = useDocuments();
  const { user } = useUserContext();
  const { t } = useTranslation();

  const canManageFramework =
    user.role === PlatformRole.superAdmin ||
    user.role === PlatformRole.coordinator;

  const [selectedId, setSelectedId] = useState<number>();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [hideCategories, setHideCategories] = useState(false);
  const [groupByGraphs, setGroupByGraphs] = useState(false);
  const [showDrafts, setShowDrafts] = useState(false);

  const chartFilters = useDocumentFilters({
    documents: documents ?? [],
    searchParams,
    setSearchParams,
    multiSelectFields: CHART_MULTI_SELECT_FILTER_KEYS,
    dateFields: CHART_DATE_FILTER_KEYS,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const documentRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  if (!documents || !categories || !tiers) {
    return <CenterSpinner withText />;
  }

  const preFilteredCategories =
    user.role === PlatformRole.reader || user.role === PlatformRole.contributor
      ? categories.filter((d) => user.categories?.some((ud) => ud.id === d.id))
      : categories;

  const filteredDocuments = filterDocuments(
    documents,
    chartFilters.activeFilters,
  );

  const chartDocumentsAndLinkedDocuments = filteredDocuments.filter(
    (document) => showDrafts || document.latestPublishedVersion !== null,
  );

  // Do not show untiered documents in chart
  const chartDocuments = chartDocumentsAndLinkedDocuments.filter(
    (document) => document.tier !== null,
  );

  const mode = groupByGraphs ? "graph" : "grid";

  const selectedCategoryIds = chartFilters.getInValues("category");
  const isSelected = (category: CategoryType) =>
    selectedCategoryIds.includes(category.id.toString());
  const visibleCategories =
    selectedCategoryIds.length > 0
      ? preFilteredCategories.filter(isSelected)
      : preFilteredCategories;

  const documentsInCategory = (category: CategoryType) =>
    chartDocuments.filter((document) => document.category.id === category.id);

  const oneColumnPerCategory = visibleCategories.map((category) => ({
    category,
    data: getChartData(documentsInCategory(category), tiers, mode),
  }));

  const singleColumnWithAllDocuments = [
    { data: getChartData(chartDocuments, tiers, mode) },
  ];

  const columnGroups: { category?: CategoryType; data: ChartData }[] =
    hideCategories ? singleColumnWithAllDocuments : oneColumnPerCategory;

  const linkedDocumentsContextValue = {
    documentsById: new Map(
      chartDocumentsAndLinkedDocuments.map((document) => [
        document.id,
        document,
      ]),
    ),
    tierOrderById: new Map(tiers.map((tier, order) => [tier.id, order])),
  };

  const selectedDocument = selectedId
    ? chartDocuments.find((document) => document.id === selectedId)
    : undefined;

  return (
    <>
      <div className="space-y-4 px-6 py-7">
        <HeadingPage title={t(($) => $.chart.header)} />

        <Toolbar
          left={
            <>
              <FilterToggleButton
                isOpen={isFiltersOpen}
                onToggle={() => setIsFiltersOpen((v) => !v)}
              />
              <SwitchWithLabel
                label={t(($) => $.chart.groupByGraphs)}
                checked={groupByGraphs}
                onCheckedChange={setGroupByGraphs}
              />
              <SwitchWithLabel
                label={t(($) => $.chart.hideCategories)}
                checked={hideCategories}
                onCheckedChange={setHideCategories}
              />
            </>
          }
          right={
            <>
              {(user.role === PlatformRole.coordinator ||
                user.role === PlatformRole.superAdmin) && (
                <TagsModal
                  trigger={
                    <Button variant="outline">
                      {t(($) => $.modal.tags.manageTags)}
                    </Button>
                  }
                />
              )}
              {canManageFramework && (
                <>
                  <ManageCategoriesButton />
                  <TierModal
                    trigger={
                      <Button variant="outline">
                        {`${t(($) => $.commons.buttons.create)} ${t(($) => $.commons.document.tier)}`}
                      </Button>
                    }
                  />
                </>
              )}
            </>
          }
        />

        <FilterableContentLayout
          contentClassName="space-y-2"
          filters={
            <DocumentFiltersPanel
              filters={chartFilters}
              showDrafts={showDrafts}
              onShowDraftsChange={setShowDrafts}
            />
          }
          isFiltersOpen={isFiltersOpen}
          sidebarLabel={t(($) => $.commons.filter.title)}
        >
          <DocumentActiveFilterChips filters={chartFilters} />

          <div
            ref={scrollContainerRef}
            className={`relative flex overflow-x-auto pb-7 ${hiddenNativeScrollbarClass}`}
          >
            <div className="bg-background sticky left-0 z-20 flex w-64 shrink-0 flex-col pl-7">
              <div className="h-9" />
              {tiers.map(({ id, title, description }) => (
                <div
                  className="even:bg-accent/70 odd:bg-background flex h-44 flex-col justify-center pl-4"
                  key={id}
                >
                  <div className="flex items-center gap-2 pr-4">
                    <h3 className="line-clamp-2 flex-1 font-medium wrap-break-word">
                      {title}
                    </h3>
                    {canManageFramework && (
                      <TierModal
                        tierId={id}
                        trigger={
                          <Button variant="ghost" size="icon">
                            <Info className="size-5 opacity-50" />
                          </Button>
                        }
                      />
                    )}
                  </div>
                  <div className="text-muted-foreground text-sm">
                    {description}
                  </div>
                </div>
              ))}
            </div>

            <div className="relative flex pr-7" ref={containerRef}>
              <ChartLinkedDocumentsContext.Provider
                value={linkedDocumentsContextValue}
              >
                {columnGroups.map(({ category, data }) => (
                  <ChartDocuments
                    key={category?.id ?? "all"}
                    data={data}
                    tiers={tiers}
                    hideCategories={hideCategories}
                    documentRefsMap={documentRefsMap}
                    containerRef={containerRef}
                    setSelectedId={setSelectedId}
                    canManage={canManageFramework}
                    onReorder={() => void mutateDocuments()}
                    onCategoryModalClose={() => void mutateDocuments()}
                    category={category}
                  />
                ))}
              </ChartLinkedDocumentsContext.Provider>
            </div>
          </div>

          <StickyHorizontalScrollbar
            targetRef={scrollContainerRef}
            deps={[categories, tiers]}
            className="fixed bottom-0 z-30 h-4 overflow-y-hidden"
          />
        </FilterableContentLayout>
      </div>

      {selectedDocument && (
        <DocumentMetadataSheet
          documentId={selectedDocument.id}
          documentVersion={
            selectedDocument.latestPublishedVersion ??
            selectedDocument.latestVersion
          }
          open={true}
          setOpen={() => setSelectedId(undefined)}
          onSave={mutateDocuments}
        />
      )}
    </>
  );
};

export default Chart;
